import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, Plus, Copy, Trash2, Power, KeyRound, LogOut, ArrowLeft, Lock, Eye, EyeOff,
  Clock, CalendarClock, Infinity as InfinityIcon, AlertTriangle, Pencil,
} from "lucide-react";


function AdminPasswordInput(props) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative block">
      <Input
        {...props}
        type={visible ? "text" : "password"}
        className={`${props.className || ""} pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Sembunyikan password" : "Lihat password"}
        title={visible ? "Sembunyikan password" : "Lihat password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-blue-600"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </span>
  );
}

export default function Admin() {
  const [theme, setTheme] = useState(
    localStorage.getItem("pinmaker-login-theme") || "light"
  );

  useEffect(() => {
    const syncTheme = () => {
      setTheme(localStorage.getItem("pinmaker-login-theme") || "light");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    const timer = window.setInterval(syncTheme, 500);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);
  const [authed, setAuthed] = useState(!!localStorage.getItem("pp_admin_token"));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newDurationDays, setNewDurationDays] = useState("30");
  const [newPermanent, setNewPermanent] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // rename key label
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  // expiry management dialog
  const [expiryTarget, setExpiryTarget] = useState(null);
  const [extendDays, setExtendDays] = useState("30");
  const [manualDate, setManualDate] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  // forgot-password (recovery) on login screen
  const [showReset, setShowReset] = useState(false);
  const [recCode, setRecCode] = useState("");
  const [resetNewPw, setResetNewPw] = useState("");
  const [resetConfirmPw, setResetConfirmPw] = useState("");
  const [resetting, setResetting] = useState(false);
  // recovery-code management (panel)
  const [recoveryConfigured, setRecoveryConfigured] = useState(false);
  const [newRecovery, setNewRecovery] = useState("");
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [renewalPricing, setRenewalPricing] = useState({
    starter: { original_price: 50000, discount_price: "", discount_active: false, days: 30, price: 50000 },
    pro: { original_price: 120000, discount_price: "", discount_active: false, days: 90, price: 120000 },
  });
  const [loadingRenewalPricing, setLoadingRenewalPricing] = useState(false);
  const [savingRenewalPricing, setSavingRenewalPricing] = useState(false);

  // Keep Admin expiry badges synchronized with the clock every second.
  const [expiryNow, setExpiryNow] = useState(() => Date.now());
  useEffect(() => {
    if (!authed) return undefined;
    const timer = setInterval(() => setExpiryNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [authed]);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/keys");
      setKeys(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("pp_admin_token");
        setAuthed(false);
      } else {
        toast.error("Gagal memuat daftar key");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authed) loadKeys(); }, [authed, loadKeys]);

  const loadRecoveryStatus = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/recovery-status");
      setRecoveryConfigured(!!res.data.configured);
    } catch (e) { console.error("Failed to load recovery status:", e); }
  }, []);

  useEffect(() => { if (authed) loadRecoveryStatus(); }, [authed, loadRecoveryStatus]);

  const loadRenewalPricing = useCallback(async () => {
    setLoadingRenewalPricing(true);
    try {
      const res = await adminApi.get("/admin/renewal-pricing");
      const packages = res.data?.packages || {};
      setRenewalPricing((current) => ({
        starter: {
          ...current.starter,
          ...(packages.starter || {}),
          discount_price: packages.starter?.discount_price ?? "",
        },
        pro: {
          ...current.pro,
          ...(packages.pro || {}),
          discount_price: packages.pro?.discount_price ?? "",
        },
      }));
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("pp_admin_token");
        setAuthed(false);
      } else {
        toast.error(err?.response?.data?.detail || "Gagal memuat harga paket");
      }
    } finally {
      setLoadingRenewalPricing(false);
    }
  }, []);

  useEffect(() => { if (authed) loadRenewalPricing(); }, [authed, loadRenewalPricing]);

  const updateRenewalPricing = (planId, patch) => {
    setRenewalPricing((current) => ({
      ...current,
      [planId]: { ...current[planId], ...patch },
    }));
  };

  const saveRenewalPricing = async () => {
    setSavingRenewalPricing(true);
    try {
      const packages = Object.fromEntries(
        ["starter", "pro"].map((planId) => {
          const item = renewalPricing[planId];
          return [planId, {
            original_price: Number(item.original_price),
            discount_price: item.discount_price === "" ? null : Number(item.discount_price),
            discount_active: Boolean(item.discount_active),
          }];
        })
      );
      const res = await adminApi.put("/admin/renewal-pricing", { packages });
      const returned = res.data?.packages || {};
      setRenewalPricing((current) => ({
        starter: { ...current.starter, ...(returned.starter || {}), discount_price: returned.starter?.discount_price ?? "" },
        pro: { ...current.pro, ...(returned.pro || {}), discount_price: returned.pro?.discount_price ?? "" },
      }));
      toast.success("Harga paket berhasil disimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan harga paket");
    } finally {
      setSavingRenewalPricing(false);
    }
  };

  const formatRupiah = (value) =>
    `Rp${Math.max(0, Number(value) || 0).toLocaleString("id-ID")}`;

  const login = async (e) => {
    e.preventDefault();
    if (!password.trim()) { toast.error("Masukkan password admin"); return; }
    setLoggingIn(true);
    try {
      const res = await adminApi.post("/admin/login", { password });
      localStorage.setItem("pp_admin_token", res.data.token);
      setPassword("");
      setAuthed(true);
      toast.success("Login admin berhasil");
    } catch (err) {
      toast.error("Password admin salah");
    } finally {
      setLoggingIn(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const days = newPermanent ? null : (parseInt(newDurationDays, 10) || 0);
      const res = await adminApi.post("/admin/keys", {
        label: newLabel.trim() || "Tanpa label",
        duration_days: days && days > 0 ? days : null,
      });
      setNewLabel("");
      setKeys((k) => [res.data, ...k]);
      toast.success("API key baru dibuat");
    } catch {
      toast.error("Gagal membuat key");
    } finally {
      setGenerating(false);
    }
  };

  const openRename = (k) => { setRenameTarget(k); setRenameValue(k.label || ""); };

  const doRename = async () => {
    if (!renameTarget) return;
    const label = renameValue.trim();
    if (!label) { toast.error("Label tidak boleh kosong"); return; }
    setRenaming(true);
    try {
      const res = await adminApi.patch(`/admin/keys/${renameTarget.id}/label`, { label });
      setKeys((ks) => ks.map((k) => (k.id === renameTarget.id ? { ...k, label: res.data.label } : k)));
      toast.success("Nama key diperbarui");
      setRenameTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengganti nama");
    } finally {
      setRenaming(false);
    }
  };

  const openExpiry = (k) => {
    setExpiryTarget(k);
    setExtendDays("30");
    // prefill manual date with current expiry (as local datetime-local value) if any
    if (k.expires_at) {
      const d = new Date(k.expires_at);
      const pad = (n) => String(n).padStart(2, "0");
      setManualDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else {
      setManualDate("");
    }
  };

  const applyExpiry = async (mode) => {
    if (!expiryTarget) return;
    const body = { mode };
    if (mode === "extend") {
      const d = parseInt(extendDays, 10) || 0;
      if (d <= 0) { toast.error("Jumlah hari tidak valid"); return; }
      body.days = d;
    } else if (mode === "set") {
      if (!manualDate) { toast.error("Pilih tanggal"); return; }
      body.expires_at = new Date(manualDate).toISOString();
    }
    setSavingExpiry(true);
    try {
      const res = await adminApi.patch(`/admin/keys/${expiryTarget.id}/expiry`, body);
      setKeys((ks) => ks.map((k) => (k.id === expiryTarget.id
        ? { ...k, expires_at: res.data.expires_at, expired: res.data.expired } : k)));
      toast.success(
        mode === "permanent" ? "Key dijadikan permanen"
        : mode === "extend" ? `Diperpanjang ${body.days} hari`
        : "Tanggal masa aktif diperbarui"
      );
      setExpiryTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui masa aktif");
    } finally {
      setSavingExpiry(false);
    }
  };

  // Human-readable remaining time / status for a key
  const expiryInfo = (k) => {
    if (!k.expires_at) return { text: "Permanen", tone: "permanent", permanent: true, expired: false };
    const ms = new Date(k.expires_at).getTime() - expiryNow;
    if (ms <= 0) return { text: "Masa aktif habis", tone: "danger", permanent: false, expired: true };
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const text = totalSeconds >= 86400
      ? `Sisa ${days} hari ${hours} jam ${minutes} menit`
      : `Sisa ${hours} jam ${minutes} menit ${seconds} detik`;
    // trial / timed keys are always shown in red
    return { text, tone: "danger", permanent: false, expired: false };
  };

  const toggleProFeature = async (key) => {
    const enabled = !Boolean(key.pro_feature_enabled);

    try {
      const res = await adminApi.patch(
        `/admin/keys/${key.id}/pro-feature`,
        { enabled }
      );

      setKeys((ks) =>
        ks.map((k) =>
          k.id === key.id
            ? { ...k, pro_feature_enabled: res.data.pro_feature_enabled }
            : k
        )
      );

      toast.success(
        enabled
          ? `Fitur PRO diaktifkan untuk ${key.label || "pengguna"}.`
          : `Fitur PRO dinonaktifkan untuk ${key.label || "pengguna"}.`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Gagal memperbarui akses PRO"
      );
    }
  };

  const toggle = async (id) => {
    try {
      const res = await adminApi.patch(`/admin/keys/${id}/toggle`);
      setKeys((ks) => ks.map((k) => (k.id === id ? { ...k, active: res.data.active } : k)));
    } catch {
      toast.error("Gagal mengubah status");
    }
  };

  const remove = async () => {
    try {
      await adminApi.delete(`/admin/keys/${deleteTarget.id}`);
      setKeys((ks) => ks.filter((k) => k.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Key & datanya dihapus");
    } catch {
      toast.error("Gagal menghapus key");
    }
  };

  const copy = (key) => {
    navigator.clipboard?.writeText(key);
    toast.success("API key disalin");
  };

  const forceLogout = async (id) => {
    try {
      await adminApi.post(`/admin/keys/${id}/logout`);
      setKeys((ks) => ks.map((k) => (k.id === id ? { ...k, active_session: false } : k)));
      toast.success("Sesi perangkat dikeluarkan");
    } catch {
      toast.error("Gagal mengeluarkan sesi");
    }
  };

  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const logoutAdmin = () => {
    localStorage.removeItem("pp_admin_token");
    setAuthed(false);
    toast.success("Keluar dari admin");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!curPw) { toast.error("Masukkan password saat ini"); return; }
    if (newPw.length < 4) { toast.error("Password baru minimal 4 karakter"); return; }
    if (newPw !== confirmPw) { toast.error("Konfirmasi password tidak cocok"); return; }
    setChangingPw(true);
    try {
      await adminApi.post("/admin/change-password", { current_password: curPw, new_password: newPw });
      setCurPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Password admin berhasil diganti");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengganti password");
    } finally {
      setChangingPw(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!recCode.trim()) { toast.error("Masukkan kode rahasia"); return; }
    if (resetNewPw.length < 4) { toast.error("Password baru minimal 4 karakter"); return; }
    if (resetNewPw !== resetConfirmPw) { toast.error("Konfirmasi password tidak cocok"); return; }
    setResetting(true);
    try {
      await adminApi.post("/admin/reset-password", { recovery_code: recCode.trim(), new_password: resetNewPw });
      toast.success("Password admin berhasil direset. Silakan login.");
      setShowReset(false);
      setRecCode(""); setResetNewPw(""); setResetConfirmPw("");
      setPassword(resetNewPw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset password");
    } finally {
      setResetting(false);
    }
  };

  const saveRecovery = async () => {
    if (newRecovery.trim().length < 6) { toast.error("Kode rahasia minimal 6 karakter"); return; }
    setSavingRecovery(true);
    try {
      await adminApi.post("/admin/set-recovery", { recovery_code: newRecovery.trim() });
      setNewRecovery("");
      setRecoveryConfigured(true);
      toast.success("Kode rahasia disimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan kode");
    } finally {
      setSavingRecovery(false);
    }
  };

  if (!authed) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-100 px-4 py-16 text-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950 dark:text-slate-100">
        <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-blue-200/40" />
        <div className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full border border-blue-300/70 bg-blue-200/35 dark:bg-blue-500/15" />
        <div className="pointer-events-none absolute right-[-100px] top-1/3 h-64 w-64 rounded-full border border-blue-300/60" />
        <div className="relative z-10 w-full max-w-md">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-3xl border border-blue-100 bg-white shadow-lg shadow-blue-900/10 dark:border-slate-700 dark:bg-slate-900">
              <ShieldCheck className="h-10 w-10 text-blue-700" strokeWidth={1.8} />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight">Admin <span className="text-blue-700 dark:text-blue-400">PIN Maker</span></h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Kelola API key pengguna</p>
          </div>
          <Card className="rounded-3xl border border-white/90 bg-white/95 shadow-2xl shadow-blue-900/10 dark:border-slate-700 dark:bg-slate-900" data-testid="admin-login-card">
            {!showReset ? (
              <>
                <CardHeader className="space-y-1 pb-4">
                  <CardTitle>Login Admin</CardTitle>
                  <CardDescription>Masukkan password admin untuk melanjutkan.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={login} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin-password">Password Admin</Label>
                      <AdminPasswordInput id="admin-password" type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus autoComplete="current-password" className="h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                    </div>
                    <Button type="submit" className="h-12 w-full rounded-xl bg-blue-700 font-semibold hover:bg-blue-800" data-testid="admin-login-button" disabled={loggingIn}>
                      {loggingIn ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Masuk
                    </Button>
                  </form>
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <button type="button" onClick={() => setShowReset(true)} data-testid="admin-forgot-link" className="text-xs text-slate-500 transition-colors hover:text-blue-700 dark:text-slate-400 dark:hover:text-blue-300">Lupa password?</button>
                    <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-slate-500 transition-colors hover:text-blue-700 dark:text-slate-400 dark:hover:text-blue-300"><ArrowLeft className="h-3.5 w-3.5" /> Login pengguna</Link>
                  </div>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader className="space-y-1 pb-4">
                  <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-700" /> Reset Password</CardTitle>
                  <CardDescription>Masukkan kode rahasia darurat untuk membuat password admin baru.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={doReset} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="rec-code">Kode Rahasia</Label>
                      <Input id="rec-code" data-testid="admin-recovery-code-input" value={recCode} onChange={(e) => setRecCode(e.target.value)} autoFocus className="h-12 rounded-xl font-mono-num" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rec-new">Password baru</Label>
                      <AdminPasswordInput id="rec-new" type="password" data-testid="admin-reset-new-password-input" value={resetNewPw} onChange={(e) => setResetNewPw(e.target.value)} className="h-12 rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rec-confirm">Ulangi password baru</Label>
                      <AdminPasswordInput id="rec-confirm" type="password" data-testid="admin-reset-confirm-input" value={resetConfirmPw} onChange={(e) => setResetConfirmPw(e.target.value)} className="h-12 rounded-xl" />
                    </div>
                    <Button type="submit" className="h-12 w-full rounded-xl bg-blue-700 hover:bg-blue-800" data-testid="admin-reset-button" disabled={resetting}>
                      {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}Reset Password
                    </Button>
                  </form>
                  <div className="mt-5 text-center">
                    <button type="button" onClick={() => setShowReset(false)} data-testid="admin-reset-back" className="inline-flex items-center gap-1.5 text-xs text-slate-500 transition-colors hover:text-blue-700 dark:text-slate-400 dark:hover:text-blue-300"><ArrowLeft className="h-3.5 w-3.5" /> Kembali ke login</button>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <img src="/pin-maker-logo.png" alt="PIN Maker Compass" className="h-10 w-10 object-contain shrink-0" />
            <span className="font-display text-lg font-bold">Admin · API Keys</span>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={logoutAdmin} data-testid="admin-logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Keluar
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> Buat API Key</CardTitle>
            <CardDescription>Beri label agar mudah dikenali (mis. nama toko/pengguna).</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="new-label" className="text-xs">Label</Label>
                <Input id="new-label" placeholder="Label, mis. Toko A" value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)} data-testid="admin-new-label-input"
                  className="sm:w-56" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-days" className="text-xs">Masa aktif (hari)</Label>
                <Input id="new-days" type="number" min={1} value={newDurationDays}
                  disabled={newPermanent}
                  onChange={(e) => setNewDurationDays(e.target.value)} data-testid="admin-new-duration-input"
                  className="sm:w-36" />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch id="new-permanent" checked={newPermanent} onCheckedChange={setNewPermanent}
                  data-testid="admin-new-permanent-switch" />
                <Label htmlFor="new-permanent" className="text-sm">Permanen</Label>
              </div>
              <Button onClick={generate} className="rounded-xl" data-testid="admin-generate-button"
                disabled={generating}>
                {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                Generate Key
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Key akan otomatis kadaluarsa setelah masa aktif habis. Pilih Permanen untuk tanpa batas waktu.
            </p>
          </CardContent>
        </Card>

        <Card className="mt-6 rounded-2xl" data-testid="admin-renewal-pricing-card">
          <CardHeader>
            <CardTitle>Harga Paket Starter &amp; Pro</CardTitle>
            <CardDescription>
              Harga checkout dibaca dari server. Harga normal tetap ditampilkan jika diskon tidak aktif.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {loadingRenewalPricing ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {["starter", "pro"].map((planId) => {
                  const item = renewalPricing[planId];
                  const effective = item.discount_active && Number(item.discount_price) > 0
                    ? Number(item.discount_price)
                    : Number(item.original_price);
                  return (
                    <div key={planId} className="space-y-3 rounded-xl border bg-card p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{planId === "starter" ? "Starter · 30 hari" : "Pro · 90 hari"}</h3>
                        <span className="text-xs text-muted-foreground">IDR</span>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`renewal-${planId}-original`}>Harga normal (Rp)</Label>
                        <Input
                          id={`renewal-${planId}-original`}
                          type="number"
                          min={1000}
                          step={1000}
                          value={item.original_price}
                          onChange={(e) => updateRenewalPricing(planId, { original_price: e.target.value })}
                          data-testid={`admin-${planId}-original-price`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`renewal-${planId}-discount`}>Harga diskon (Rp)</Label>
                        <Input
                          id={`renewal-${planId}-discount`}
                          type="number"
                          min={1000}
                          step={1000}
                          value={item.discount_price}
                          onChange={(e) => updateRenewalPricing(planId, { discount_price: e.target.value })}
                          placeholder="Kosongkan jika tidak ada diskon"
                          data-testid={`admin-${planId}-discount-price`}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                        <Label htmlFor={`renewal-${planId}-discount-active`} className="text-sm">Aktifkan diskon</Label>
                        <Switch
                          id={`renewal-${planId}-discount-active`}
                          checked={Boolean(item.discount_active)}
                          onCheckedChange={(checked) => updateRenewalPricing(planId, { discount_active: checked })}
                          data-testid={`admin-${planId}-discount-switch`}
                        />
                      </div>
                      <div className="rounded-lg border border-dashed p-3 text-sm">
                        <p className="text-xs text-muted-foreground">Harga yang digunakan checkout</p>
                        {item.discount_active && (
                          <p className="text-xs text-muted-foreground line-through">{formatRupiah(item.original_price)}</p>
                        )}
                        <p className="text-lg font-bold text-primary">{formatRupiah(effective)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={saveRenewalPricing}
                className="rounded-xl"
                disabled={savingRenewalPricing || loadingRenewalPricing}
                data-testid="admin-save-renewal-pricing"
              >
                {savingRenewalPricing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Simpan Harga Paket
              </Button>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={loadRenewalPricing}
                disabled={loadingRenewalPricing || savingRenewalPricing}
              >
                Muat Ulang
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6 rounded-2xl" data-testid="admin-change-password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Ganti Password Admin</CardTitle>
            <CardDescription>Password disimpan aman (di-hash) di server dan berlaku untuk login berikutnya.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={changePassword} className="grid gap-3 sm:max-w-md">
              <div className="space-y-1.5">
                <Label htmlFor="cur-pw">Password saat ini</Label>
                <AdminPasswordInput id="cur-pw" type="password" value={curPw}
                  onChange={(e) => setCurPw(e.target.value)} data-testid="admin-current-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-pw">Password baru</Label>
                <AdminPasswordInput id="new-pw" type="password" value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} data-testid="admin-new-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw">Ulangi password baru</Label>
                <AdminPasswordInput id="confirm-pw" type="password" value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)} data-testid="admin-confirm-password-input" />
              </div>
              <Button type="submit" className="rounded-xl sm:w-fit" data-testid="admin-change-password-button"
                disabled={changingPw}>
                {changingPw ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Ganti Password
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="mt-6 rounded-2xl" data-testid="admin-recovery-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Kode Rahasia (Lupa Password)</CardTitle>
            <CardDescription>
              Kode darurat untuk mereset password admin bila lupa. Status:{" "}
              <span className={recoveryConfigured ? "font-medium text-primary" : "text-muted-foreground"} data-testid="admin-recovery-status">
                {recoveryConfigured ? "sudah diatur" : "belum diatur"}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:max-w-md">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="set-recovery">Kode rahasia baru</Label>
                <Input id="set-recovery" value={newRecovery} onChange={(e) => setNewRecovery(e.target.value)}
                  data-testid="admin-recovery-code-set-input" className="font-mono-num" placeholder="min. 6 karakter" />
              </div>
              <Button onClick={saveRecovery} className="rounded-xl" data-testid="admin-set-recovery-button"
                disabled={savingRecovery}>
                {savingRecovery ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                Simpan Kode
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6">
          <h2 className="mb-3 font-display text-lg font-semibold">Daftar Key ({keys.length})</h2>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : keys.length === 0 ? (
            <div className="rounded-2xl border border-dashed py-16 text-center text-sm text-muted-foreground">
              Belum ada key. Buat key pertama di atas.
            </div>
          ) : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div key={k.id} data-testid="admin-key-row"
                  className="flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{k.label}</span>
                      {k.active ? (
                        <Badge variant="secondary" data-testid="admin-key-status">Aktif</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground" data-testid="admin-key-status">Nonaktif</Badge>
                      )}
                      {(() => {
                        const info = expiryInfo(k);
                        const cls = info.tone === "danger"
                          ? "border-destructive/40 bg-destructive/10 text-destructive"
                          : info.tone === "permanent"
                          ? "border-blue-500/40 bg-blue-500/10 text-blue-600"
                          : info.tone === "warn"
                          ? "border-amber-500/40 bg-amber-500/10 text-amber-600"
                          : "border-border bg-secondary text-muted-foreground";
                        return (
                          <span data-testid="admin-key-expiry"
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
                            {info.permanent ? <InfinityIcon className="h-3 w-3" /> : info.expired ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                            {info.text}
                          </span>
                        );
                      })()}
                    </div>
                    <code className="mt-1 block truncate font-mono-num text-xs text-muted-foreground" data-testid="admin-key-value">
                      {k.key}
                    </code>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground" data-testid="admin-key-session">
                      <span>{k.project_count ?? 0} proyek</span>
                      {k.active_session ? (
                        <span className="inline-flex items-center gap-1 text-primary">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Sesi aktif
                        </span>
                      ) : (
                        <span>Tidak ada sesi aktif</span>
                      )}
                      {k.expires_at && <span>Berlaku s/d {fmt(k.expires_at)}</span>}
                      {k.last_device && <span>{k.last_device}</span>}
                      {k.last_login_at && <span>Login: {fmt(k.last_login_at)}</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => copy(k.key)}
                      data-testid="admin-copy-key">
                      <Copy className="mr-1.5 h-3.5 w-3.5" /> Salin
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openRename(k)}
                      data-testid="admin-rename-key">
                      <Pencil className="mr-1.5 h-3.5 w-3.5" /> Rename
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openExpiry(k)}
                      data-testid="admin-expiry-key">
                      <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Masa aktif
                    </Button>
                    <Button
                      size="sm"
                      variant={k.pro_feature_enabled ? "default" : "outline"}
                      className={`rounded-lg ${
                        k.pro_feature_enabled
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "border-blue-500/40 text-blue-600 hover:bg-blue-500/10"
                      }`}
                      onClick={() => toggleProFeature(k)}
                      data-testid="admin-toggle-pro-key"
                    >
                      {k.pro_feature_enabled
                        ? "Nonaktifkan Fitur PRO"
                        : "Aktifkan Fitur PRO"}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => forceLogout(k.id)}
                      disabled={!k.active_session} data-testid="admin-force-logout-key">
                      <LogOut className="mr-1.5 h-3.5 w-3.5" /> Keluarkan
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => toggle(k.id)}
                      data-testid="admin-toggle-key">
                      <Power className="mr-1.5 h-3.5 w-3.5" /> {k.active ? "Nonaktifkan" : "Aktifkan"}
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-lg text-destructive" onClick={() => setDeleteTarget(k)}
                      data-testid="admin-delete-key">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Rename key dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="admin-rename-dialog">
          <DialogHeader>
            <DialogTitle>Ganti nama key</DialogTitle>
            <DialogDescription>Ubah label agar mudah dikenali (mis. nama toko/pelanggan).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-label">Label baru</Label>
            <Input id="rename-label" value={renameValue} autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doRename(); }}
              data-testid="admin-rename-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Batal</Button>
            <Button onClick={doRename} disabled={renaming} data-testid="admin-rename-confirm">
              {renaming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pencil className="mr-2 h-4 w-4" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expiry management dialog */}
      <Dialog open={!!expiryTarget} onOpenChange={(o) => !o && setExpiryTarget(null)}>
        <DialogContent data-testid="admin-expiry-dialog">
          <DialogHeader>
            <DialogTitle>Masa aktif · {expiryTarget?.label}</DialogTitle>
            <DialogDescription>
              {expiryTarget?.expires_at
                ? `Saat ini berlaku sampai ${fmt(expiryTarget?.expires_at)}.`
                : "Saat ini permanen (tanpa batas waktu)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Extend */}
            <div className="rounded-xl border p-3">
              <Label className="text-xs font-semibold">Perpanjang</Label>
              <p className="mb-2 text-xs text-muted-foreground">
                Tambah durasi dari sisa masa aktif (atau dari hari ini bila sudah kadaluarsa).
              </p>
              <div className="flex items-center gap-2">
                <Input type="number" min={1} value={extendDays}
                  onChange={(e) => setExtendDays(e.target.value)}
                  data-testid="admin-extend-days-input" className="w-28" />
                <span className="text-sm text-muted-foreground">hari</span>
                <Button size="sm" className="ml-auto rounded-lg" disabled={savingExpiry}
                  onClick={() => applyExpiry("extend")} data-testid="admin-extend-confirm">
                  <Clock className="mr-1.5 h-3.5 w-3.5" /> Perpanjang
                </Button>
              </div>
            </div>
            {/* Set manual date */}
            <div className="rounded-xl border p-3">
              <Label className="text-xs font-semibold">Set tanggal manual</Label>
              <p className="mb-2 text-xs text-muted-foreground">Tentukan tanggal & jam kadaluarsa persis.</p>
              <div className="flex items-center gap-2">
                <Input type="datetime-local" value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  data-testid="admin-set-date-input" className="flex-1" />
                <Button size="sm" variant="secondary" className="rounded-lg" disabled={savingExpiry}
                  onClick={() => applyExpiry("set")} data-testid="admin-set-date-confirm">
                  <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> Set
                </Button>
              </div>
            </div>
            {/* Permanent */}
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <Label className="text-xs font-semibold">Jadikan permanen</Label>
                <p className="text-xs text-muted-foreground">Hapus batas waktu sepenuhnya.</p>
              </div>
              <Button size="sm" variant="outline" className="rounded-lg" disabled={savingExpiry}
                onClick={() => applyExpiry("permanent")} data-testid="admin-make-permanent">
                <InfinityIcon className="mr-1.5 h-3.5 w-3.5" /> Permanen
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExpiryTarget(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus key "{deleteTarget?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Ini permanen dan menghapus SEMUA proyek &amp; gambar milik key ini.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
